
<template>
  <div class="middle" :class="{ 'editor-canvas': isEditing, 'is-panning': panning, 'over-component': panOverComponent }" :style="runtimeSurfaceStyle" v-loading="loadingData" :element-loading-text="loadingText" element-loading-background="rgba(11, 21, 29, .94)" :aria-busy="loadingData ? 'true' : 'false'"
       @mousedown.capture="handleStageMouseDown" @wheel.capture="handleStageWheel" @contextmenu="handleStageContextMenu" @dragover="handleDragOver" @drop="handleDrop">
    <p v-if="loadingData" class="canvas-loading-status" role="status" aria-live="polite">{{ loadingText }}</p>
    <div v-if="loadError" class="canvas-load-error" role="alert">
      <p>{{ contain.isTemplatePreview ? '页面模板加载失败' : '大屏配置加载失败' }}</p>
      <p>{{ loadError }}</p>
      <el-button size="small" @click="initData">重新加载</el-button>
    </div>
    <editor-guides v-if="isEditing" :viewport="contain.editorViewport" :size="viewportSize" :config="contain.config"
                   :scope="JSON.stringify([$route.name, $route.params.id, contain.activePageId])" :disabled="loadingData || !!loadError" @begin="beginGuideInteraction" />
    <div id="wrapper"
         v-show="!loadError"
         class="wrapper"
         :style="wrapperStyle">
      <div class="content"
           id="content"
           :style="contentStyle"
           ref="content">
        <div class="container"
             :style="styleName"
             id="container"
             tabindex="-1" :aria-label="!isEditing ? contain.currentPageName : '编辑画布'"
             @click.capture="handlePreviewClick"
             ref="container">
          <div class="grade"
               v-if="isEditing && (gradeFlag || contain.config.gradeShow)"
               :style="gradeLenStyle"></div>
          <subgroup ref="subgroup" :key="contain.activePageId"
                    :nav="contain.list"></subgroup>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import subgroup from './subgroup'
import editorGuides from './guides.vue'
import { getProjectDraft, getProjectRuntime } from '@/api/project-versions'
import { projectDraftResult } from '@/utils/project-versions'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { readPageProject } from '@/schema/page-project'
import { canvasFilterStyle } from '@/schema/page-config'
import { config as defaultConfig } from '@/option/config'
import { canvasViewport, fitCanvasZoom, normalizeZoom, centeredCanvas, screenToCanvas, zoomViewportAt } from '@/editor/core/viewport'
import { componentSize, componentRect, limitNodeBounds } from '@/editor/core/component-size'
import { locateNode, canEditNode } from '@/editor/core/node-operations'
import baseList from '@/option/base'
import { imgOption } from '@/option/config'
import { resolveComponent, imageComponent, COMPONENT_DRAG_TYPE, IMAGE_DRAG_TYPE } from '@/editor/core/palette-catalog'
import { resourceImageFromDrag, RESOURCE_IMAGE_DRAG_TYPE } from '@/editor/core/resource-image'
import { previewNodes, readPreviewSnapshot } from '@/runtime/preview-snapshot'
import { scrollFleetList } from '@/components/FleetList/scroll'
import { TEMPLATE_DRAG_TYPE, TEMPLATE_RENDERERS, templateFromDrag, instantiateTemplate, templateLeaves } from '@/editor/core/component-template'
import { uuid } from '@/utils/utils'
import store from '@/store'
import { getTemplate } from '@/api/templates'
import { getTemplate as getComponentResource } from '@/api/component-resources'
import { normalizeComponentResource } from '@/utils/component-resources'
import { COMPONENT_RESOURCE_DRAG_TYPE, componentResourceFromDrag } from '@/editor/core/component-resource'
import { templateResult } from '@/utils/templates'
import { readTemplateEditor, readTemplatePreview, templateRouteKind } from '@/editor/core/template-editor'
import { templatePreviewNodes } from '@/runtime/template-preview'
import { sharedViewNodes } from '@/runtime/shared-view'
import { sharedControlCurrent } from '@/runtime/device-control'
import { pageNavigationAction } from '@/components/Navigation/pages'
import backgroundDrafts from '@/runtime/background-drafts'
export default {
  name: 'contents',
  inject: ["contain"],
  provide () {
    return {
      contain: this.contain,
      container: this
    };
  },
  components: {
    subgroup, editorGuides
  },
  data () {
    return {
      selectCount: {},
      viewportSize: { width: 0, height: 0 },
      panning: false,
      panOverComponent: false,
      loadingData: false,
      loadingSlow: false,
      loadingPassword: false,
      loadError: '',
      gradeFlag: false,
      sub: []
    }
  },
  computed: {
    loadingText () {
      if (this.loadingPassword) return '正在等待查看密码验证…'
      const name = this.contain.isComponentResourceEditing ? '组件样式' : this.contain.isTemplatePreview ? '页面模板' : '大屏配置'
      return this.loadingSlow ? '服务器响应较慢，仍在读取' + name + '，请稍候…' : '正在加载' + name + '…'
    },
    isEditing () {
      return ['build', 'component-resource-edit', 'template-component-edit', 'template-page-edit', 'template-project-edit'].includes(this.$route.name) && this.contain.menuFlag !== false
    },
    viewport () {
      return canvasViewport(this.contain.config, this.contain.editorViewport.zoom, this.isEditing, this.viewportSize, this.contain.isMenuEmbedded === true)
    },
    runtimeSurfaceStyle () {
      if (this.isEditing || this.contain.isMenuEmbedded !== true) return {}
      return {
        backgroundColor: this.contain.config.backgroundColor || '#1e1f1e',
        overflow: ['等宽', '等高'].includes(this.contain.config.fitType) ? 'auto' : 'hidden'
      }
    },
    stepScale () {
      return 1 / this.viewport.zoom
    },
    wrapperStyle () {
      if (this.isEditing) return {}
      return {
        width: this.setPx(Math.max(this.viewport.width, this.viewportSize.width)),
        height: this.setPx(Math.max(this.viewport.height, this.viewportSize.height))
      }
    },
    contentStyle () {
      if (this.isEditing) return {
        width: this.setPx(this.viewport.width), height: this.setPx(this.viewport.height),
        left: this.setPx(this.contain.editorViewport.panX), top: this.setPx(this.contain.editorViewport.panY)
      }
      return {
        width: this.setPx(this.viewport.width),
        height: this.setPx(this.viewport.height),
        marginLeft: this.setPx(this.viewport.offsetX),
        marginTop: this.setPx(this.viewport.offsetY)
      }
    },
    //计算中央可视化大屏比例
    styleName () {
      const scale = this.viewport.zoom;
      return Object.assign({
        transform: `scale(${this.viewport.scaleX === undefined ? scale : this.viewport.scaleX}, ${this.viewport.scaleY === undefined ? scale : this.viewport.scaleY})`,
        width: this.setPx(this.contain.config.width),
        height: this.setPx(this.contain.config.height),
        backgroundColor: this.contain.config.backgroundColor || '#1e1f1e',
        filter: canvasFilterStyle(this.contain.config.filter)
      }, (() => {
        if (this.contain.config.backgroundImage) {
          return {
            backgroundImage: `url(${JSON.stringify(this.contain.config.backgroundImage)})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }
        }
        return
      })())
    },
    gradeLenStyle () {
      return {
        backgroundSize: `${this.setPx(this.contain.config.gradeLen)} ${this.setPx(this.contain.config.gradeLen)},${this.setPx(this.contain.config.gradeLen)} ${this.setPx(this.contain.config.gradeLen)}`
      }
    },
  },
  mounted () {
    this.setResize();
    this.initData();
    this.initFun();
    window.addEventListener('resize', this.setResize);
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(this.setResize);
      this.resizeObserver.observe(this.$el);
    }
  },
  watch: {
    '$route.params.id': 'initData',
    '$route.name': 'initData',
    '$route.query.preview': 'initData',
    '$route.query.page': 'initData',
    'contain.editorViewport.zoom' (zoom) {
      if (this.isEditing && !this.zoomAnimation && zoom !== this._wheelZoomTarget) this.centerCanvas()
      this._wheelZoomTarget = null
    }
  },
  methods: {
    openWarning(){//警告弹框
      if(this.contain.config.warnType === 0){
        this.sub[0] = window.getWebSocket(
            "device-alarm-product",
            "/rule-engine/device/alarm/*/*/*", {}).subscribe((resp) => {
          const {payload} = resp;
          this.$notify({
            title: '设备警告',
            type: 'warning',
            // duration: 0,
            message: '设备[' + payload.deviceName + ']发生告警：[' + payload.alarmName + ']'
          })
        })
      }else{
        if(this.contain.config.deviceList.length <= 0){
          return
        }
        if(this.contain.config.warnType === 2){
          this.contain.config.deviceList.forEach((i, index) => {
            this.sub[index] = window.getWebSocket(
                "device-alarm-"+i.id,
                "/rule-engine/device/alarm/*/"+i.id+"/*", {}).subscribe((resp) => {
              const {payload} = resp;
              this.$notify({
                title: '设备警告',
                type: 'warning',
                // duration: 0,
                message: '设备[' + payload.deviceName + ']发生告警：[' + payload.alarmName + ']'
              })
            })
          })
        }else{
          this.contain.config.deviceList.forEach((i, index) => {
            this.sub[index] = window.getWebSocket(
                "device-alarm-"+ i.id,
                "/rule-engine/device/alarm/"+i.productId+"/"+i.id+"/*", {}).subscribe((resp) => {
              const {payload} = resp;
              this.$notify({
                title: '产品警告',
                type: 'warning',
                // duration: 0,
                message: '产品[' + payload.productName + ']发生告警：[' + payload.alarmName + ']'
              })
            })
          })
        }
      }
    },
    initFun () {
      ['handleRefresh', 'handleGetObj'].forEach(ele => {
        // 切页会销毁旧组件树，不能继续持有旧 subgroup 的绑定方法。
        this[ele] = (...args) => this.$refs.subgroup && this.$refs.subgroup[ele](...args)
      });
    },
    //初始化数据
    async initData () {
      const id = this.contain.isSharedView ? this.contain.shareProjectId : this.$route.params.id;
      if (this.isEditing && this.contain.resetEditorSaveTracking) this.contain.resetEditorSaveTracking();
      this.contain.id = id;
      if (this.loadCancellation) this.loadCancellation.cancel();
      this.loadCancellation = createRequestCancellation();
      const request = this.loadCancellation;
      this.loadingData = true;
      this.loadingSlow = false;
      this.loadingPassword = false;
      clearTimeout(this.loadSlowTimer);
      this.loadSlowTimer = setTimeout(() => { if (this.loadCancellation === request && !this._isDestroyed) this.loadingSlow = true; }, 6000);
      this.loadError = '';
      this.contain.projectRecord = null;
      this.contain.projectVersionStatus = null;
      this.contain.templateRecord = null;
      this.contain.templateSaveBlocked = '';
      this.contain.pageProject = { version: 1, homePageId: '', pages: [] };
      this.contain.activePageId = '';
      if (this.contain.resetPageNavigation) this.contain.resetPageNavigation();
      this.contain.nav = [];
      this.contain.handleInitActive();
      this.clearSubscriptions();
      try {
        let record = { id, name: '' };
        let templateModel;
        let projectStatus = null;
        if (this.contain.isTemplateEditing) {
          const permission = this.contain.isComponentResourceEditing ? 'visualizationComponent' : 'visualizationTemplate';
          if (!store.getters['auth/hasResourcePermission'](permission + ':query') || !store.getters['auth/hasResourcePermission'](permission + ':save')) throw new Error('当前账号没有资源编辑权限');
          const response = await (this.contain.isComponentResourceEditing ? getComponentResource : getTemplate)(id, { cancelToken: request.token });
          if (this.contain.isComponentResourceEditing) normalizeComponentResource(templateResult(response));
          templateModel = readTemplateEditor(templateResult(response), defaultConfig);
          if (templateModel.template.kind !== templateRouteKind(this.$route.name)) throw new Error('模板类型与编辑入口不一致');
          if (templateModel.template.id !== id) throw new Error('模板ID与请求不一致，请重新加载');
          record = templateModel.record;
        } else if (this.contain.isTemplatePreview) {
          if (!store.getters['auth/hasResourcePermission']('visualizationTemplate:query')) throw new Error('当前账号没有模板查询权限');
          const response = await getTemplate(id, { cancelToken: request.token });
          templateModel = readTemplatePreview(templateResult(response), defaultConfig);
          if (templateModel.template.kind !== templateRouteKind(this.$route.name)) throw new Error('模板类型与预览入口不一致');
          if (templateModel.template.id !== id) throw new Error('模板ID与请求不一致，请重新加载');
          record = templateModel.record;
        } else if (this.contain.isSharedView) {
          const response = await this.contain.loadSharedRecord({ cancelToken: request.token });
          record = response.data.result;
          if (!record || record.id !== id) throw new Error('分享配置与项目不一致');
        } else if (this.contain.isPreview) {
          record = readPreviewSnapshot(this.$route.query.preview, id);
        } else if (id) {
          const response = await (this.isEditing ? getProjectDraft(id, { cancelToken: request.token }) : getProjectRuntime(id, { cancelToken: request.token, params: { publishedOnly: this.contain.isMenuEmbedded === true } }));
          if (this.isEditing) { const draft = projectDraftResult(response, id); record = draft.record; projectStatus = draft.status; }
          else record = response.data.result;
          if (!record || !record.id) throw new Error('服务器未返回有效的大屏配置');
        }
        if (this.loadCancellation !== request || this._isDestroyed) return;
        if (!this.isEditing && !this.contain.isPreview && (record.state === 'disabled' || (record.state && record.state.value === 'disabled'))) throw new Error('该大屏已禁用，请在大屏列表启用后再访问');
        // 返回尚在后台保存的项目时，优先恢复内存快照，避免短暂读到旧草稿。
        const backgroundDraft = this.isEditing && !this.contain.isTemplateEditing ? backgroundDrafts.read(id, projectStatus) : null;
        if (backgroundDraft) { record = { ...backgroundDraft.record, metadata: backgroundDraft.latest.metadata }; projectStatus = backgroundDraft.status; }
        const model = templateModel || readPageProject(record, defaultConfig, this.contain.isPreview || this.isEditing ? this.$route.query.page : undefined);
        if (!this.isEditing && !this.contain.isPreview && model.visual.password) {
          this.loadingPassword = true;
          // 旧查看页仅保留兼容提示；正式分享鉴权将在后端发布阶段替换。
          await this.$prompt('请输入密码', '提示', {
            confirmButtonText: '确定', showCancelButton: false, showClose: false,
            closeOnClickModal: false,
            inputValidator: value => value === model.visual.password || '密码不正确，请重新输入'
          });
        }
        if (this.loadCancellation !== request || this._isDestroyed) return;
        this.contain.projectRecord = model.record;
        this.contain.projectVersionStatus = projectStatus;
        this.contain.templateRecord = templateModel ? templateModel.template : null;
        this.contain.obj = model.metadata;
        this.contain.visual = model.visual;
        this.contain.pageProject = model.pageProject;
        this.contain.activePageId = model.activePageId;
        this.contain.config = model.config;
        this.contain.nav = this.contain.isSharedView ? sharedViewNodes(model.nodes, this.contain.isSharedDataView, this.contain.shareControlAvailable === true) : this.contain.isTemplatePreview ? model.nodes : (this.contain.isPreview && model.metadata.templatePreview === true) ? templatePreviewNodes(model.nodes, { runtimeSource: true }) : this.contain.isPreview ? previewNodes(model.nodes, { liveDeviceData: true }) : model.nodes;
        if (this.contain.resetPageNavigation) this.contain.resetPageNavigation();
        if (this.isEditing) this.contain.resetEditorHistory();
        if (this.isEditing && this.contain.isComponentTemplateEditing) {
          const first = templateLeaves(this.contain.nav)[0];
          this.contain.active = first ? [first.index] : [];
          this.contain.inspectorVisible = true;
          this.contain.tabsActive = '0';
        }
        this.calcData();
        if (this.isEditing && !this.contain.isTemplateEditing && this.$route.query.component) {
          const component = this.contain.list.find(node => String(node.index) === this.$route.query.component);
          if (component) { this.contain.active = [component.index]; this.contain.inspectorVisible = true; this.$nextTick(() => { this.contain.tabsActive = ['0', '1', '2'].includes(this.$route.query.tab) ? this.$route.query.tab : '0'; }); }
        }
        if (this.isEditing && this.contain.initializeEditorSaveTracking) this.contain.initializeEditorSaveTracking();
        if (backgroundDraft) {
          this.contain.editorSavedSignature = backgroundDraft.savedSignature;
          this.contain.editorSaveError = backgroundDraft.error;
          if (backgroundDraft.running && this.contain.$refs.top) this.contain.$refs.top.handleBuild({ quiet: true, background: true });
        }
        this.setScale(this.$el.clientWidth);
        if (!this.isEditing && !this.contain.isPreview) {
          if (model.config.mark.show) this.watermark(model.config.mark);
          if (model.config.isWarning) this.openWarning();
        }
      } catch (error) {
        if (this.loadCancellation === request && !isRequestCanceled(error) && !this._isDestroyed) {
          this.loadError = error.message || '大屏配置加载失败，请稍后重试';
        }
      } finally {
        if (this.loadCancellation === request) {
          clearTimeout(this.loadSlowTimer);
          this.loadingData = false;
          this.loadingPassword = false;
        }
      }
    },
    //适配尺寸
    setResize () {
      const previous = this.viewportSize;
      const inset = this.isEditing ? 22 : 0;
      this.viewportSize = { width: Math.max(0, this.$el.clientWidth - inset), height: Math.max(0, this.$el.clientHeight - inset) };
      if (this.isEditing && previous.width && previous.height) {
        this.contain.editorViewport.panX += (this.viewportSize.width - previous.width) / 2;
        this.contain.editorViewport.panY += (this.viewportSize.height - previous.height) / 2;
      }
      this.contain.contentWidth = this.viewportSize.width;
    },
    //计算比例
    setScale () {
      this.setResize();
      if (this.isEditing) this.zoomTo(fitCanvasZoom(this.viewportSize, this.contain.config), this.loadingData ? 0 : 240);
    },
    centerCanvas () {
      Object.assign(this.contain.editorViewport, centeredCanvas(this.viewportSize, this.contain.config, this.contain.editorViewport.zoom));
    },
    zoomTo (percent, duration = 240) {
      this.stopPan();
      if (this.zoomAnimation) cancelAnimationFrame(this.zoomAnimation);
      const target = normalizeZoom(percent);
      const viewport = this.contain.editorViewport;
      const initial = { zoom: viewport.zoom, panX: viewport.panX, panY: viewport.panY };
      const end = centeredCanvas(this.viewportSize, this.contain.config, target);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) duration = 0;
      const started = performance.now();
      const frame = now => {
        const progress = duration ? Math.min(1, (now - started) / duration) : 1;
        const eased = 1 - Math.pow(1 - progress, 3);
        viewport.zoom = initial.zoom + (target - initial.zoom) * eased;
        viewport.panX = initial.panX + (end.panX - initial.panX) * eased;
        viewport.panY = initial.panY + (end.panY - initial.panY) * eased;
        if (progress < 1) this.zoomAnimation = requestAnimationFrame(frame);
        else { this.zoomAnimation = null; viewport.zoom = target; }
      };
      this.zoomAnimation = requestAnimationFrame(frame);
    },
    beginGuideInteraction () {
      this.stopPan()
      if (this.zoomAnimation) { cancelAnimationFrame(this.zoomAnimation); this.zoomAnimation = null }
      if (this.contain.$refs.contentmenu) this.contain.$refs.contentmenu.hide()
    },
    handleStageMouseDown (event) {
      if (!this.isEditing || this.loadError || this.loadingData) return;
      if (event.button === 0 && event.target.closest('[data-editor-guides]')) return;
      const component = event.target.closest('.avue-draggable');
      if (event.button === 1) {
        event.preventDefault();
        event.stopPropagation();
        if (this.zoomAnimation) { cancelAnimationFrame(this.zoomAnimation); this.zoomAnimation = null; }
        this.panStart = { x: event.clientX, y: event.clientY, ...this.contain.editorViewport };
        this.panning = true;
        this.panOverComponent = !!component;
        this.contain.$refs.contentmenu.hide();
        window.addEventListener('mousemove', this.movePan);
        window.addEventListener('mouseup', this.stopPan);
        window.addEventListener('blur', this.stopPan);
      } else if (event.button === 0) {
        if (!component) this.contain.handleInitActive();
        else {
          const item = locateNode(this.contain.nav, component.dataset.nodeId);
          if (item && (item.node.locked || item.ancestors.some(node => node.locked))) this.contain.active = [item.node.index];
        }
      }
    },
    handleStageWheel (event) {
      if (this.isEditing && event.ctrlKey) {
        if (event.defaultPrevented || event.cancelable === false || !Number.isFinite(event.deltaY) || !event.deltaY ||
            event.shiftKey || Math.abs(event.deltaX || 0) > Math.abs(event.deltaY)) return
        event.preventDefault()
        event.stopPropagation()
        if (this.loadingData || this.loadError || this.contain.editorVersionSwitching || this.panning || event.buttons) return
        if (this.zoomAnimation) { cancelAnimationFrame(this.zoomAnimation); this.zoomAnimation = null }
        const viewport = this.contain.editorViewport, rect = this.$refs.container.getBoundingClientRect()
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewportSize.height || 800 : 1
        const delta = Math.max(-100, Math.min(100, event.deltaY * unit))
        const next = zoomViewportAt(viewport, viewport.zoom * Math.exp(-delta * .0015), { x: event.clientX - rect.left, y: event.clientY - rect.top })
        if (next.zoom === viewport.zoom) return
        this._wheelZoomTarget = next.zoom
        Object.assign(viewport, next)
        return
      }
      const layer = event.target.closest('.avue-draggable[data-node-id]')
      if (!layer) return
      const found = locateNode(this.contain.nav, layer.getAttribute('data-node-id'))
      if (!found || !['productList', 'deviceList'].includes(found.node.component.prop)) return
      scrollFleetList(event, layer)
    },
    movePan (event) {
      if (!this.panning) return;
      event.preventDefault();
      this.contain.editorViewport.panX = this.panStart.panX + event.clientX - this.panStart.x;
      this.contain.editorViewport.panY = this.panStart.panY + event.clientY - this.panStart.y;
      const element = document.elementFromPoint(event.clientX, event.clientY);
      this.panOverComponent = !!(element && element.closest('.avue-draggable'));
    },
    stopPan () {
      this.panning = false;
      this.panOverComponent = false;
      window.removeEventListener('mousemove', this.movePan);
      window.removeEventListener('mouseup', this.stopPan);
      window.removeEventListener('blur', this.stopPan);
    },
    handleStageContextMenu (event) {
      if (!this.isEditing) return;
      event.preventDefault();
      if (!event.target.closest('.avue-draggable')) this.contain.handleContextMenu(event);
    },
    handleDragOver (event) {
      if (!this.isEditing) return;
      if (Array.from(event.dataTransfer.types).some(type => [COMPONENT_DRAG_TYPE, IMAGE_DRAG_TYPE, RESOURCE_IMAGE_DRAG_TYPE, TEMPLATE_DRAG_TYPE, COMPONENT_RESOURCE_DRAG_TYPE].includes(type))) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    handleDrop (event) {
      if (!this.isEditing) return;
      event.preventDefault();
      if (!this.contain.projectRecord) return;
      const item = resolveComponent(baseList, event.dataTransfer.getData(COMPONENT_DRAG_TYPE));
      const template = componentResourceFromDrag(event.dataTransfer.getData(COMPONENT_RESOURCE_DRAG_TYPE)) || templateFromDrag(event.dataTransfer.getData(TEMPLATE_DRAG_TYPE));
      const option = item ? item.option : resourceImageFromDrag(baseList, event.dataTransfer.getData(RESOURCE_IMAGE_DRAG_TYPE)) || imageComponent(baseList, imgOption, event.dataTransfer.getData(IMAGE_DRAG_TYPE));
      if (!option && !template) return;
      const rect = this.$refs.container.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        this.$message.info('请将组件拖到画布内');
        return;
      }
      const point = screenToCanvas({ x: event.clientX, y: event.clientY }, { left: rect.left, top: rect.top, zoom: this.contain.editorViewport.zoom });
      if (template) this.addTemplate(template, point);
      else this.addComponent(option, point);
    },
    addTemplate (item, point) {
      const resource = item && item.resourceType === 'component';
      if (!this.isEditing || !this.contain.projectRecord || !store.getters['auth/hasResourcePermission']((resource ? 'visualizationComponent' : 'visualizationTemplate') + ':query')) return;
      if (resource) { try { item = normalizeComponentResource(item); } catch (error) { this.$message.error(error.message); return; } }
      if (item.kind === 'page') {
        if (this.contain.isComponentTemplateEditing) { this.$message.warning('组件样式编辑不应用页面模板，请从组件模板添加'); return; }
        return this.contain.applyPageTemplate(item);
      }
      try {
        const snapshot = item.snapshot;
        if (!point) point = {
          x: (this.viewportSize.width / 2 - this.contain.editorViewport.panX) / this.viewport.zoom - snapshot.width / 2,
          y: (this.viewportSize.height / 2 - this.contain.editorViewport.panY) / this.viewport.zoom - snapshot.height / 2
        };
        const position = { x: Math.round(Math.max(0, Math.min(point.x, this.contain.config.width - snapshot.width))), y: Math.round(Math.max(0, Math.min(point.y, this.contain.config.height - snapshot.height))) };
        const instantiated = instantiateTemplate(snapshot, uuid, position);
        const nodes = this.contain.isComponentTemplateEditing ? templatePreviewNodes(instantiated, { runtimeSource: true }) : instantiated;
        limitNodeBounds(nodes, this.contain.config);
        this.contain.rememberStructure();
        this.contain.nav.unshift(...nodes);
        this.contain.active = nodes.map(node => node.index);
        this.$message.success('已添加独立模板副本，请配置数据源与交互');
      } catch (error) { this.$message.error(error.message); }
    },
    addComponent (option, point) {
      if (!this.contain.projectRecord) return;
      if (this.contain.isComponentTemplateEditing && (!TEMPLATE_RENDERERS.includes(option.component.name) || option.component.name !== option.component.prop)) { this.$message.warning('此组件暂不支持配置样式模板'); return; }
      option = { ...option, component: { ...option.component, ...componentSize(option.component, this.contain.config) } };
      if (!point) point = {
        x: (this.viewportSize.width / 2 - this.contain.editorViewport.panX) / this.viewport.zoom - option.component.width / 2,
        y: (this.viewportSize.height / 2 - this.contain.editorViewport.panY) / this.viewport.zoom - option.component.height / 2
      };
      const left = Math.round(Math.max(0, Math.min(point.x, this.contain.config.width - option.component.width)));
      const top = Math.round(Math.max(0, Math.min(point.y, this.contain.config.height - option.component.height)));
      this.contain.handleAdd({ ...option, left, top }, true);
    },
    handlePreviewClick (event) {
      if (!this.isEditing) {
        const layer = event.target.closest('[data-node-id]');
        const found = layer && locateNode(this.contain.nav, layer.getAttribute('data-node-id'));
        const node = found && found.node;
        if (node && node.component && node.component.name === 'navigation' && node.component.prop === 'navigation' && node.option && node.option.mode === 'pages') {
          const panel = event.target.closest('.screen-page-navigation');
          const scroller = event.target.closest('button[data-page-nav-scroll]');
          if (panel && layer.contains(panel) && scroller && panel.contains(scroller)) return;
          event.preventDefault(); event.stopImmediatePropagation();
          const button = event.target.closest('button[data-page-target]');
          if (panel && layer.contains(panel) && button && panel.contains(button) && !button.disabled && this.contain.runPageNavigation) {
            try {
              const action = pageNavigationAction(node.navigation, this.contain.pageProject.pages, button.getAttribute('data-page-target'));
              this.contain.runPageNavigation({ ...node, navigation: action });
            } catch (error) { this.contain.navigationError = error.message; }
          }
          return;
        }
        // 本地地图只有缩放、聚合和只读信息窗。预览放行画布原生点击，不开放脚本或整层导航。
        const mapCanvas = event.target.closest('.device-location-map__canvas');
        if (node && node.component && node.component.name === 'map' && node.component.prop === 'map' &&
            (Number(node.dataType) === 5 || Number((node.option || {}).type) !== 1) && mapCanvas && layer.contains(mapCanvas)) return;
        if (node && node.component && node.component.name === 'deviceGroup' && node.component.prop === 'deviceGroup' &&
            !this.contain.isTemplatePreview && !(this.contain.obj && this.contain.obj.templatePreview === true) &&
            ((!this.contain.isPreview && !this.contain.isSharedView) ||
             (this.contain.isPreview && this.$route.name === 'view' && !this.contain.isSharedView) ||
             (this.$route.name === 'screen-share' && sharedControlCurrent(this.contain)))) {
          const control = event.target.closest('button[data-group-control], input[data-group-control], select[data-group-control]');
          const panel = control && control.closest('.screen-device-group');
          if (control && panel && layer.contains(panel) && panel.contains(control)) return;
        }
        // 属性设置有独立确认和权限校验，不让整层导航拦截其原生表单。
        if (node && node.component && node.component.name === 'deviceProperty' && node.component.prop === 'deviceProperty' &&
            !this.contain.isPreview && !this.contain.isSharedView && event.target.closest('.screen-device-property')) return;
        if (node && node.component && node.component.name === 'deviceProperty' && node.component.prop === 'deviceProperty' &&
            ((this.contain.isPreview && this.$route.name === 'view' && !this.contain.isSharedView) || (this.$route.name === 'screen-share' && sharedControlCurrent(this.contain))) && !this.contain.isTemplatePreview &&
            !(this.contain.obj && this.contain.obj.templatePreview === true)) {
          // 普通预览只放行组件内建控制与重连，权限仍由控制接口核验，不放行整层导航或脚本。
          const control = event.target.closest('input[data-device-control], select[data-device-control], button[data-device-control]');
          const panel = control && control.closest('.screen-device-property');
          if (control && panel && layer.contains(panel) && panel.contains(control)) return;
        }
        const chartRetry = event.target.closest('button[data-device-chart-retry]');
        const statusRetry = event.target.closest('button[data-device-status]');
        if (node && node.component && node.component.name === 'deviceStatus' && node.component.prop === 'deviceStatus' && statusRetry && layer.contains(statusRetry) && statusRetry.closest('.screen-device-status')) return;
        if (node && Number(node.dataType) === 2 && ['line', 'bar', 'text', 'gauge'].includes(node.component.prop) &&
            node.component.name === node.component.prop && chartRetry &&
            layer.contains(chartRetry) && chartRetry.closest('.device-chart-feedback')) return;
        const listType = node && ['productList', 'deviceList'].includes(node.component.prop);
        // 只放行列表内建的只读控件；是否配置行导航不影响搜索、分页和重试。
        const control = event.target.closest('input[data-fleet-control], button[data-fleet-control], button[data-fleet-retry]');
        if (listType && control && layer.contains(control) && control.closest('.screen-fleet-list')) return;
        const action = node && (node.component.prop === 'navigation' ? { type: 'back' } : node.navigation);
        const row = listType && event.target.closest('[data-fleet-row]');
        if (this.contain.runPageNavigation && action && action.type !== 'none' && (!listType || row)) {
          if (listType) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.contain.runPageNavigation({
              ...node,
              data: {
                ...(node.data || {}),
                row: {
                  id: row.getAttribute('data-fleet-row-id'),
                  productId: row.getAttribute('data-fleet-product-id')
                }
              },
              navigation: action
            });
            return;
          }
          // 捕获阶段阻断原点击处理，导航不能顺便触发旧脚本或设备命令。
          event.preventDefault();
          event.stopImmediatePropagation();
          this.contain.runPageNavigation({ ...node, navigation: action });
          return;
        }
      }
      if (this.contain.isPreview) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    clearSubscriptions () {
      this.sub.forEach(subscription => { if (subscription) subscription.unsubscribe(); });
      this.sub = [];
    },
    calcData () {
      if (!this.contain.config.mark) this.contain.config.mark = {}
      if (!this.contain.config.query) this.contain.config.query = {}
    },
    handlePostionSelect (postion) {
      if (!this.contain.active.every(id => {
        const item = locateNode(this.contain.nav, id);
        return canEditNode(item) && item.node.component;
      })) {
        this.$message.info('请仅选择未锁定的组件进行对齐，分组请先解散');
        return;
      }
      this.contain.rememberStructure();
      this.handleCalcPostionSelect();
      const x1 = this.selectCount.maxx1;
      const x2 = this.selectCount.maxx2;
      const y1 = this.selectCount.maxy1;
      const y2 = this.selectCount.maxy2;
      if (postion === 'left') {
        this.handleMoveSelectList(x1, undefined, true, postion);
      } else if (postion === 'center') {
        this.handleMoveSelectList(x1 + (x2 - x1) / 2, undefined, true, postion);
      } else if (postion === 'right') {
        this.handleMoveSelectList(x2, undefined, true, postion);
      } else if (postion === 'top') {
        this.handleMoveSelectList(undefined, y1, true, postion);
      } else if (postion === 'middle') {
        this.handleMoveSelectList(undefined, y1 + (y2 - y1) / 2, true, postion);
      } else if (postion === 'bottom') {
        this.handleMoveSelectList(undefined, y2, true, postion);
      }
    },
    handleMoveSelectList (left, top, type, postion) {
      this.contain.active.forEach(ele => {
        if (!canEditNode(locateNode(this.contain.nav, ele))) return;
        ele = this.contain.findlist(ele)
        const ele_component = ele.component;
        const objects = this.handleGetObj(ele.index);
        const draggable = objects && objects[0];
        //水平情况
        if (left !== undefined) {
          let baseLeft = Number(type ? left : (Number(ele.left) + left).toFixed(2));
          if (postion === 'right') {
            baseLeft = baseLeft - ele_component.width
          }
          else if (postion === 'center') {
            const obj_center = Number(ele.left) + ele_component.width / 2;
            baseLeft = Number(ele.left) + (left - obj_center)
          }
          this.$set(ele, 'left', baseLeft);
          if (draggable) draggable.setLeft(baseLeft);
        }
        //垂直情况
        if (top !== undefined) {
          let baseTop = Number(type ? top : (Number(ele.top) + top).toFixed(2));
          if (postion === 'bottom') {
            baseTop = baseTop - ele_component.height
          }
          else if (postion === 'middle') {
            const obj_middle = Number(ele.top) + ele_component.height / 2;
            baseTop = Number(ele.top) + (top - obj_middle)
          }
          this.$set(ele, 'top', baseTop)
          if (draggable) draggable.setTop(baseTop);
        }
        const bounded = componentRect({ ...ele_component, left: ele.left, top: ele.top }, this.contain.config);
        this.$set(ele, 'left', bounded.left); this.$set(ele, 'top', bounded.top);
        this.$set(ele_component, 'width', bounded.width); this.$set(ele_component, 'height', bounded.height);
        if (draggable) {
          draggable.setLeft(bounded.left); draggable.setTop(bounded.top);
          draggable.baseWidth = bounded.width; draggable.baseHeight = bounded.height;
        }
      })
    },
    //计算多选状态下的最大边界值
    handleCalcPostionSelect () {
      this.selectCount.maxx1 = Infinity;
      this.selectCount.maxy1 = Infinity;
      this.selectCount.maxx2 = -Infinity;
      this.selectCount.maxy2 = -Infinity;
      this.contain.active.forEach(ele => {
        ele = this.contain.findlist(ele)
        const left = Number(ele.left);
        const top = Number(ele.top);
        const width = Number(ele.component.width);
        const height = Number(ele.component.height);
        if (this.selectCount.maxx1 > left) {
          this.selectCount.maxx1 = left;
        }
        if (this.selectCount.maxx2 < left + width) {
          this.selectCount.maxx2 = left + width;
        }
        if (this.selectCount.maxy1 > top) {
          this.selectCount.maxy1 = top;
        }
        if (this.selectCount.maxy2 < top + height) {
          this.selectCount.maxy2 = top + height;
        }
      })
    },
  },
  beforeDestroy() {
    clearTimeout(this.loadSlowTimer);
    this.stopPan();
    if (this.zoomAnimation) cancelAnimationFrame(this.zoomAnimation);
    if (this.loadCancellation) this.loadCancellation.cancel();
    window.removeEventListener('resize', this.setResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.clearSubscriptions();
  }
}
</script>

<style>
.avue-echart-line, .avue-echart-bar, .avue-echart-text, .avue-echart-gauge { position: relative; }
/* 高于查看页 z-index:9 的导航遮罩，异常时重试不能变成页面跳转。 */
.device-chart-feedback { position: absolute; inset: 0; z-index: 10; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; box-sizing: border-box; padding: 12px; color: #93b2c2; background: rgba(9, 23, 33, .86); font-size: 14px; text-align: center; overflow-wrap: anywhere; }
.device-chart-feedback button { padding: 6px 16px; border: 1px solid #3685a3; border-radius: 4px; color: #d8f4ff; background: #15445a; cursor: pointer; }
.device-chart-feedback button:focus-visible { outline: 2px solid #57cdeb; outline-offset: 3px; }
.device-chart-hint { margin: 5px 0 0; color: var(--editor-muted, #8ca7b8); font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
.device-chart-config-error { color: #eaa7a7; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
.editor-theme--light .device-chart-config-error { color: #a12727; }
.device-chart-data-settings .el-form-item__content > .el-input + .el-input, .device-chart-data-settings .el-form-item__content > .el-date-editor + .el-date-editor { margin-top: 6px; }
.device-chart-data-settings .el-form-item__content > .el-date-editor.el-input { width: 100%; }
.canvas-loading-status { position: absolute; width: 1px; height: 1px; margin: 0; overflow: hidden; clip-path: inset(50%); }
.middle .el-loading-spinner .el-loading-text { line-height: 1.6; padding: 0 20px; white-space: normal; }
.middle .el-loading-spinner .circular { width: 28px; height: 28px; }
.middle .el-loading-spinner .path { stroke: #43b9ee; stroke-width: 3; stroke-dasharray: 80, 200; stroke-linecap: round; animation: none; }
.middle .el-loading-spinner .el-loading-text { margin-top: 14px; color: #aac0cf; font-size: 14px; }
@media (prefers-reduced-motion: reduce) { .middle .el-loading-spinner .circular, .middle .el-loading-spinner .path { animation: none; } }
.canvas-load-error {
  position: relative;
  z-index: 1;
  padding: 24px;
  color: #f5b9b9;
  font-size: 14px;
}
.canvas-load-error p { margin-bottom: 12px; }
</style>
